import React, { useState, useCallback, useMemo, memo } from "react";
import {
  Box,
  Typography,
  Radio,
  RadioGroup,
  FormControlLabel,
  IconButton,
  FormControl,
  FormLabel,
  Button,
  Tooltip,
} from "@mui/material";
import { ReactComponent as IconClose } from "assets/svg/20-close.svg";
import CustomPopover from "components/common/CustomPopover";
import pxToRem from "theme/base/pxToRem";
import { LeaveTypeOption } from "../style";
import { useDispatch, useSelector } from "react-redux";
import {
  minDateSelector,
  selectFormReqLeave,
  selectListReasonLeave,
  selectReasonLeave,
  selectRequestLeaveDetailData,
} from "modules/DeclareLeave/redux/selector";
import moment from "moment";
import { ReactComponent as IconPlus } from "assets/svg/20-plus.svg";

import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import {
  calculateTotalLeaveDay,
  formReqLeaveUpdate,
  updateTotalLeaveDay,
} from "modules/DeclareLeave/redux/formReqLeave";
import {
  getReasonLeaveStart,
  TYPE_LEAVE,
} from "modules/DeclareLeave/redux/getReasonLeave";
import { optionTypeLeave } from "../const";
import { TypeSelect } from "modules/DeclareLeave/redux/formReqArrivingLateLeaveEarly";
import {
  getLeaveManagerReqStart,
  updateFormReqLeaveManager,
} from "modules/DeclareLeave/redux/getLeaveManagerReq";
import { hexToRgba } from "theme/types/color";
import { useLegacyEffect } from "helper/customHook";

export interface AddLeaveProps {}

export const colors = {
  [TYPE_LEAVE.FULL_DAY]: {
    color: "#000",
    backgroundColor: "#E4EBFF",
    border: "#182BCF",
  },
  [TYPE_LEAVE.HALF_DAY]: {
    color: "#000",
    backgroundColor: "#F2F2F2",
    border: "#B3B3B3",
  },
};

const AddLeave = (props: any) => {
  const { data } = useSelector(selectFormReqLeave);
  const dataDetail = useSelector(selectRequestLeaveDetailData);
  const listReasonLeave = useSelector(selectListReasonLeave);
  const { FULL_DAY, HALF_DAY } = useSelector(selectReasonLeave);
  const minDate = useSelector(minDateSelector);

  const getYear = props?.year || moment(new Date()).year();
  const fullDate = `${props.date}/${getYear}`;
  const dispatch = useDispatch();

  // ngày đã đăng kí trên lịch
  const dateData = useMemo(() => {
    return data?.details?.find(
      (it) =>
        it.fromDate?.toISOString() ===
        moment(fullDate, "DD/MM/YYYY")?.toISOString()
    );
  }, [data, fullDate]);

  // chi tiết đơn có tồn tại ngày đã đăng kí không => boolean
  const isIncludes = useMemo(() => {
    return dataDetail?.details?.some(
      (it) =>
        moment(it?.fromDate, "YYYY-MM-DD")?.toISOString() ===
        moment(dateData?.fromDate, "DD/MM/YYYY")?.toISOString()
    );
  }, [dataDetail, dateData]);

  const listReason = useMemo(() => {
    if (!dateData?.type || !isIncludes) return listReasonLeave;
    if (dateData?.type === TYPE_LEAVE.FULL_DAY) return FULL_DAY;
    return HALF_DAY;
  }, [dateData, isIncludes, FULL_DAY, HALF_DAY, listReasonLeave]);

  const labelActive = (isTypeLeave: boolean) => {
    if (!dateData) return <></>;
    if (isTypeLeave)
      return (
        <Box
          display={"flex"}
          justifyContent={"space-between"}
          alignItems={"center"}
          width={"100%"}
        >
          <Typography
            sx={{
              fontSize: "12px",
              color:
                (isDisable() && data?.type !== TypeSelect.LONG_TERM) ||
                (!dateData?.isNew && !isIncludes)
                  ? "#B3B3B3"
                  : "#000",
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
              paddingRight: "5px",
              maxWidth: {
                xs: pxToRem(60),
                lg: pxToRem(80),
                xxl: pxToRem(100),
              },
            }}
          >
            {dateData?.reasonName}
          </Typography>{" "}
          {data?.type !== TypeSelect.LONG_TERM && (
            <KeyboardArrowDownIcon
              fontSize="large"
              sx={{
                color:
                  isDisable() || (!dateData?.isNew && !isIncludes)
                    ? "#B3B3B3"
                    : "#000",
              }}
            />
          )}
        </Box>
      );
    return (
      <Box
        display={"flex"}
        justifyContent={"space-between"}
        alignItems={"center"}
        width={"100%"}
      >
        <Typography
          sx={{
            fontSize: "12px",
            color:
              (isDisable() && data?.type !== TypeSelect.LONG_TERM) ||
              (!dateData?.isNew && !isIncludes)
                ? "#B3B3B3"
                : "#000",
          }}
        >
          {dateData?.type === TYPE_LEAVE.FULL_DAY
            ? "Nghỉ cả ngày"
            : "Nghỉ nửa ngày"}
        </Typography>{" "}
        {data?.type !== TypeSelect.LONG_TERM && (
          <KeyboardArrowDownIcon
            fontSize="large"
            sx={{
              color:
                isDisable() || (!dateData?.isNew && !isIncludes)
                  ? "#B3B3B3"
                  : "#000",
            }}
          />
        )}
      </Box>
    );
  };

  const labelDefault = () => {
    return (
      <Box
        display={"flex"}
        gap={pxToRem(6)}
        alignItems={"center"}
        sx={{
          svg: {
            width: pxToRem(16),
            height: pxToRem(16),
          },
        }}
      >
        <IconPlus />
        Thêm
      </Box>
    );
  };

  const isDisable = () => {
    const { minRegisterDate } = minDate || {};

    if (!fullDate) return true; // Nếu ngày không hợp lệ thì disable
    const givenDate = moment(fullDate, "DD/MM/YYYY"); // Chuyển đổi ngày nhập vào moment

    if (!givenDate.isValid()) return true; // Nếu ngày không hợp lệ thì disable

    const today = moment(minRegisterDate); // Lấy ngày tối thiểu có thể chọn

    if (givenDate.isBefore(today, "day")) return true; // Nếu ngày chọn trước ngày đã cài đặt ở api => disabled

    if (props?.isOutsideCurrentMonth) return true; // nếu ngày chọn không thuộc tháng đang hiện thị ở calendar => disabled

    if (data?.type === TypeSelect.LONG_TERM) return true; // Nếu là đki nghỉ nhiều ngày => disabled

    // trường hợp chỉnh sửa đơn
    if (dataDetail && dataDetail?.details && dataDetail?.details?.length) {
      const isIncludes = dataDetail?.details?.some(
        (it) =>
          moment(it?.fromDate, "YYYY-MM-DD")?.toISOString() ===
          moment(dateData?.fromDate, "DD/MM/YYYY")?.toISOString()
      );
      // disabled khi ngày chọn không có trong đơn
      return !isIncludes;
    }
    // mặc định disabled khi ngày đó có id
    return !!dateData?.id;
  };

  const handleGetManager = useCallback(
    (data: any[]) => {
      const total = calculateTotalLeaveDay(data);
      dispatch(updateTotalLeaveDay(total));
      dispatch(updateFormReqLeaveManager(data));

      dispatch(getLeaveManagerReqStart());
    },
    [dispatch]
  );

  const handleTypeChange = useCallback(
    (e) => {
      const value = e.target?.value;
      const dateIdx = data?.details?.findIndex(
        (it) =>
          it.fromDate?.toISOString() ===
          moment(fullDate, "DD/MM/YYYY")?.toISOString()
      );
      const newData = data?.details?.map((it, idx) =>
        idx === dateIdx
          ? {
              ...it,
              type: value,
              isNew: true,
              reasonCode: "",
              leaveReasonTypeName: "",
              reasonName: "",
              leaveReasonType: "",
              requireAttachFiles: false,
              leaveReasonTypeMap: {},
            }
          : it
      );
      dispatch(
        formReqLeaveUpdate({
          details: newData,
        })
      );
      const listData = newData.filter((detail) => {
        if (dataDetail?.id) {
          return (
            dataDetail?.details?.some(
              (it) =>
                moment(it?.fromDate)?.toISOString() ===
                detail?.fromDate?.toISOString()
            ) && detail?.reasonCode
          );
        }
        return detail?.isNew && detail?.reasonCode;
      });
      handleGetManager(listData);
      dispatch(getReasonLeaveStart(value));
    },

    [data.details, fullDate]
  );

  const handleChangeLeaveType = useCallback(
    (e) => {
      const value = e.target?.value;
      const reasonIdx = listReasonLeave.find(
        (it) => it.leaveReasonCode === value
      );
      const dateIdx = data?.details?.findIndex(
        (it) =>
          it.fromDate?.toISOString() ===
          moment(fullDate, "DD/MM/YYYY")?.toISOString()
      );

      const newData = data?.details?.map((it, idx) =>
        idx === dateIdx
          ? {
              ...it,
              reasonCode: value,
              leaveReasonTypeName: reasonIdx?.leaveReasonTypeName,
              reasonName: reasonIdx?.leaveReasonName,
              requireAttachFiles: reasonIdx?.requireAttachFiles,
              leaveReasonType: reasonIdx?.leaveReasonType,
              hexColor: reasonIdx?.hexColor,
              isNew: true,
              leaveReasonTypeMap: reasonIdx?.leaveReasonTypeMap || {},
            }
          : it
      );
      dispatch(
        formReqLeaveUpdate({
          files: [],
          files_files: [],
          files_file_contents: [],
          details: newData,
        })
      );
      const listData = newData.filter((detail) => {
        if (dataDetail?.id) {
          return (
            dataDetail?.details?.some(
              (it) =>
                moment(it?.fromDate)?.toISOString() ===
                detail?.fromDate?.toISOString()
            ) && detail?.reasonCode
          );
        }
        return detail?.isNew && detail?.reasonCode;
      });
      handleGetManager(listData);
    },

    [listReasonLeave, data, fullDate, dataDetail, handleGetManager]
  );

  const getBackgroundColor = (
    disabled: boolean,
    type: TypeSelect,
    color: string
  ) => {
    const isIncludes = dataDetail?.details?.some(
      (it) =>
        moment(it?.fromDate, "YYYY-MM-DD")?.toISOString() ===
        moment(dateData?.fromDate, "DD/MM/YYYY")?.toISOString()
    );
    if (
      !disabled ||
      (type === TypeSelect.LONG_TERM && (isIncludes || dateData?.isNew))
    )
      return `${color || "#E4EBFF"} !important`;
    return "#E6E6E6";
  };

  useLegacyEffect(() => {
    if (!dateData?.type || !isIncludes || data?.type === TypeSelect.LONG_TERM)
      return;
    // ** lấy danh sách lý do nghỉ cho đơn ngắn ngày
    dispatch(getReasonLeaveStart(dateData?.type));
  }, [dateData, isIncludes, data?.type]);

  return (
    <Box
      display={"flex"}
      justifyContent={"space-between"}
      flexDirection={"column"}
      gap={2}
      width={"100%"}
      alignItems={"center"}
    >
      {dateData && dateData?.type ? (
        <>
          <CustomPopover
            styleButton={{
              backgroundColor: getBackgroundColor(
                isDisable(),
                data?.type,
                colors[dateData?.type]?.backgroundColor
              ),
              border: "none",
              borderLeft: `2px solid ${colors[dateData?.type]!?.border} !important`,
              borderTopLeftRadius: "2px !important",
              borderBottomLeftRadius: "2px !important",

              "&:disabled": {
                ...(data?.type === TypeSelect.LONG_TERM && {
                  color: "#000 !important",
                  border: "none !important",
                  borderLeft: `2px solid ${colors[dateData?.type]!?.border} !important`,
                  borderTopLeftRadius: "2px !important",
                  borderBottomLeftRadius: "2px !important",
                }),
              },
              "&:hover": {
                // backgroundColor: `${typeReasonColor[colorIndex(ReqType.LeavingEarly) || 0]} !important`,
                border: "solid 1px  #182BCF !important",
                borderLeft: `2px solid ${colors[dateData?.type]!?.border} !important`,
              },
            }}
            buttonLabel={labelActive(false)}
            props={{ ...props }}
            disabled={isDisable()}
          >
            {({ props, onClose }) => (
              <Box sx={{ padding: "6px 12px 6px 0", borderRadius: pxToRem(8) }}>
                <FormControl component="fieldset" fullWidth>
                  <RadioGroup
                    row
                    value={dateData?.type}
                    onChange={(e) => {
                      handleTypeChange(e);
                      onClose();
                    }}
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      padding: 0,
                      justifyContent: "start",
                      alignItems: "start",
                    }}
                  >
                    {Object.values(optionTypeLeave).map((duration) => (
                      <Tooltip
                        title={duration.label || ""}
                        key={duration.value}
                        placement="right-end"
                      >
                        <FormControlLabel
                          key={duration.value}
                          value={duration.value}
                          control={<Radio />}
                          label={duration.label}
                          sx={(theme) => ({
                            ".Mui-checked": {
                              color: "#182BCF !important", // Label color when checked
                            },
                            margin: 0,
                            span: {
                              fontSize: pxToRem(12),
                            },
                          })}
                        />
                      </Tooltip>
                    ))}
                  </RadioGroup>
                </FormControl>
              </Box>
            )}
          </CustomPopover>
          <CustomPopover
            styleButton={{
              color: isDisable() ? "#B3B3B3" : "#000",
              backgroundColor: getBackgroundColor(
                isDisable(),
                data?.type,
                hexToRgba(dateData?.hexColor || "#ffffff", 0.1)
              ),
              border: `solid 1px  ${isDisable() ? "#B3B3B3" : hexToRgba(dateData?.hexColor || "#ffffff", 1)} !important`,
            }}
            buttonLabel={labelActive(true)}
            props={{ ...props }}
            disabled={isDisable()}
          >
            {({ props, onClose }) => (
              <Box sx={{ padding: "16px", borderRadius: pxToRem(8) }}>
                <FormControl component="fieldset" fullWidth>
                  <RadioGroup
                    row
                    value={dateData?.reasonCode}
                    onChange={(e) => {
                      handleChangeLeaveType(e);
                      onClose();
                    }}
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      padding: 0,
                      maxHeight: "200px",
                      overflowY: "scroll",
                      flexWrap: "nowrap",
                    }}
                  >
                    {listReason?.map((type) => (
                      <Tooltip
                        title={type.leaveReasonName || ""}
                        key={type.leaveReasonCode}
                        placement="right-end"
                      >
                        <FormControlLabel
                          key={type.leaveReasonCode}
                          value={type.leaveReasonCode}
                          control={<Radio />}
                          label={
                            <Typography
                              sx={{
                                whiteSpace: "nowrap",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                fontSize: "14px",
                                maxWidth: pxToRem(120),
                              }}
                            >
                              {type.leaveReasonName}
                            </Typography>
                          }
                          sx={{
                            ".Mui-checked": {
                              color: "#182BCF !important", // Label color when checked
                            },
                            span: {
                              fontSize: pxToRem(12),
                            },
                          }}
                        />
                      </Tooltip>
                    ))}
                  </RadioGroup>
                </FormControl>
              </Box>
            )}
          </CustomPopover>
        </>
      ) : (
        <CustomPopover
          buttonLabel={labelDefault()}
          props={{ ...props }}
          disabled={isDisable()}
          styleButton={{
            border: "0",
            background: "#F2F2F2",
            marginTop: pxToRem(3),
          }}
        >
          {({ props, onClose }) => (
            <PopupContent
              props={props}
              onClose={onClose}
              handleGetManager={handleGetManager}
            />
          )}
        </CustomPopover>
      )}
    </Box>
  );
};

const PopupContent = memo(
  ({
    props,
    onClose,
    handleGetManager,
  }: {
    props: any;
    onClose: () => void;
    handleGetManager: (data: any[]) => void;
  }) => {
    const dispatch = useDispatch();
    const { data } = useSelector(selectFormReqLeave);
    const listReasonLeave = useSelector(selectListReasonLeave);
    const listDataDetails = data?.details;

    const [reqData, setReqData] = useState({
      type: TYPE_LEAVE.FULL_DAY,
      reasonCode: "",
      leaveReasonTypeName: "",
      requireAttachFiles: false,
      leaveReasonType: "",
      hexColor: "",
      leaveReasonTypeMap: {},
    });

    const getYear = props?.year || moment(new Date()).year();
    useLegacyEffect(() => {
      const fullDate = `${props.date}/${getYear}`;
      const dateIdx = data?.details?.find(
        (it) =>
          it.fromDate?.toISOString() ===
          moment(fullDate, "DD/MM/YYYY")?.toISOString()
      );

      if (!dateIdx) return;
      setReqData((prev) => ({
        ...prev,
      }));
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [data, props.date, getYear]);

    useLegacyEffect(() => {
      dispatch(getReasonLeaveStart(TYPE_LEAVE.FULL_DAY as any));
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleConfirm = useCallback(
      (e: React.MouseEvent<HTMLButtonElement>) => {
        e.preventDefault();
        e.stopPropagation();
        const fullDate = `${props.date}/${getYear}`;
        const dateIdx = listDataDetails?.findIndex(
          (it) =>
            it.fromDate?.toISOString() ===
            moment(fullDate, "DD/MM/YYYY")?.toISOString()
        );

        if (dateIdx > -1) {
          dispatch(
            formReqLeaveUpdate({
              details: listDataDetails?.map((it, idx) =>
                idx === dateIdx
                  ? {
                      toDate: moment(fullDate, "DD/MM/YYYY"),
                      fromDate: moment(fullDate, "DD/MM/YYYY"),
                      ...reqData,
                      isNew: true,
                    }
                  : it
              ),
            })
          );
        } else {
          dispatch(
            formReqLeaveUpdate({
              details: [
                ...data?.details,
                {
                  toDate: moment(fullDate, "DD/MM/YYYY"),
                  fromDate: moment(fullDate, "DD/MM/YYYY"),
                  ...reqData,
                  isNew: true,
                },
              ],
            })
          );
        }

        // lấy danh sách người duyệt
        const dataDetails = [
          ...data?.details.filter((it) => !!it.isNew),
          {
            toDate: moment(fullDate, "DD/MM/YYYY"),
            fromDate: moment(fullDate, "DD/MM/YYYY"),
            ...reqData,
            isNew: true,
          },
        ];
        handleGetManager(dataDetails);
        // Your confirm logic here
        onClose();
        setReqData({
          type: TYPE_LEAVE.FULL_DAY,
          reasonCode: "",
          leaveReasonTypeName: "",
          requireAttachFiles: false,
          leaveReasonType: "",
          hexColor: "",
          leaveReasonTypeMap: {},
        });
      },
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [data, reqData, props.date, dispatch]
    );

    const handleTypeChange = useCallback((e) => {
      const value = e.target?.value;
      setReqData((prev) => ({
        ...prev,
        type: value,
        reasonCode: "",
      }));
      dispatch(getReasonLeaveStart(value));
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleChangeLeaveType = (e) => {
      const value = e.target?.value;
      const reasonIdx = listReasonLeave.find(
        (it) => it.leaveReasonCode === value
      );

      setReqData((prev) => ({
        ...prev,
        reasonCode: value,
        leaveReasonTypeName: reasonIdx?.leaveReasonTypeName,
        reasonName: reasonIdx?.leaveReasonName,
        requireAttachFiles: reasonIdx?.requireAttachFiles,
        leaveReasonType: reasonIdx?.leaveReasonType,
        hexColor: reasonIdx?.hexColor,
        leaveReasonTypeMap: reasonIdx?.leaveReasonTypeMap || {},
      }));
    };

    return (
      <Box sx={{ padding: "16px", overflowX: "auto" }}>
        <Box display={"flex"} justifyContent={"space-between"}>
          <Typography
            variant="subtitle1"
            fontWeight="bold"
            textAlign="center"
            gutterBottom
          >
            Ngày {props?.date}
          </Typography>
          <IconButton onClick={onClose}>
            <IconClose />
          </IconButton>
        </Box>
        <Box width={pxToRem(450)}>
          {/* Leave Duration Selection */}
          <FormControl component="fieldset" fullWidth margin="normal">
            <RadioGroup
              row
              value={reqData.type}
              onChange={handleTypeChange}
              sx={{
                display: "grid",
                gridTemplateColumns: "repeat(2, 1fr)",
                gap: pxToRem(8),
              }}
            >
              {Object.values(optionTypeLeave).map((duration) => (
                <FormControlLabel
                  key={duration.value}
                  value={duration.value}
                  control={<Radio />}
                  label={duration.label}
                  sx={(theme) => ({
                    border: `1px solid `,
                    ".Mui-checked": {
                      color: `${theme.palette.stroke.strokeBrand} !important`, // Label color when checked
                    },

                    borderRadius: pxToRem(4),
                    margin: 0,
                    span: {
                      fontSize: pxToRem(12),
                    },
                    borderColor:
                      reqData.type === duration.value
                        ? `#182BCF`
                        : `${theme.palette.stroke.strokeDefault}`,
                  })}
                />
              ))}
            </RadioGroup>
          </FormControl>

          {/* Leave Type Selection */}
          <FormControl component="fieldset" fullWidth margin="normal">
            <FormLabel
              sx={(theme) => ({
                marginBottom: pxToRem(16),
                color: theme.palette.text.textPrimary,
              })}
            >
              Chọn loại nghỉ{" "}
            </FormLabel>
            <LeaveTypeOption
              value={reqData.reasonCode}
              onChange={handleChangeLeaveType}
            >
              {listReasonLeave.map((type) => (
                <FormControlLabel
                  key={type.leaveReasonCode}
                  value={type.leaveReasonCode}
                  control={<Radio />}
                  label={type.leaveReasonName}
                  sx={(theme) => ({
                    ".Mui-checked": {
                      color: theme.palette.stroke.strokeBrand, // Label color when checked
                    },
                    span: {
                      fontSize: pxToRem(14),
                    },
                  })}
                />
              ))}
            </LeaveTypeOption>
          </FormControl>

          {/* Display Selected Leave Type */}
          <Box
            sx={(theme) => ({
              backgroundColor: reqData?.leaveReasonTypeName
                ? hexToRgba(
                    reqData.hexColor || theme.palette.background.bgTertiary,
                    0.1
                  )
                : theme.palette.background.bgTertiary,
              padding: `${pxToRem(10)} ${pxToRem(12)}`,
              borderRadius: pxToRem(4),
              marginTop: 2,
              display: "flex",
              justifyContent: "space-between",
            })}
          >
            <Typography
              fontSize={pxToRem(13)}
              width={pxToRem(90)}
              sx={{ color: "#666666" }}
            >
              Loại hình nghỉ:{" "}
            </Typography>
            <Typography flex={1} fontSize={pxToRem(13)} textAlign={"right"}>
              {reqData?.leaveReasonTypeName || "----"}
            </Typography>
          </Box>
        </Box>
        <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
          <Button
            variant="contained"
            color="primary"
            fullWidth
            sx={{ minWidth: 120 }}
            onClick={handleConfirm}
            disabled={!reqData?.reasonCode}
          >
            Xác nhận
          </Button>
        </Box>
      </Box>
    );
  }
);

export default AddLeave;
